import axios from 'axios';

import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom';

// Redux.
import { connect } from 'react-redux';
import Loading from './Loading'

import SpeakerHeader from './SpeakerHeader';
import TrackInfoRow from './TrackInfoRow';
import AudioPlayerDrawer from './AudioPlayerDrawer';
import { loadTrack } from './audioHelper';
import { eventLog } from './eventHelper';
import SearchBoxWithDefaultSuggestions from './SearchBoxWithDefaultSuggestions';
import AlgoliaSpeakerSearch from './AlgoliaSpeakerSearch';
import SearchSuggestions from './SearchSuggestions';
import useEntitySearchSuggestions from './useEntitySearchSuggestions';
import FullWidthButton from './FullWidthButton';
import HighlightsDrawer from './HighlightsDrawer';
import { SearchActivityProvider } from './SearchActivity';


const EntityInfo = (props) => {
    const {
        missingSpeaker,
        speakerId,
        persistQueryInUrl = true,
    } = props;

    const [info, setInfo] = useState(null);
    const { suggestions: defaultSuggestions, isLoading: suggestionsLoading } = useEntitySearchSuggestions(
        speakerId ? `/speaker_search_suggestions/${speakerId}` : null,
    );
    const [searchParams, setSearchParams] = useSearchParams();
    const [localQuery, setLocalQuery] = useState('');
    const [showHighlights, setShowHighlights] = useState(false);
    const [highlightSearchEntry, setHighlightSearchEntry] = useState(0);
    const searchContainerRef = useRef(null);

    const getCleanedQuery = (params) => {
        const rawQuery = params.get('q');
        return rawQuery ? rawQuery.trim() : '';
    }
    const query = persistQueryInUrl ? getCleanedQuery(searchParams) : localQuery;

    useEffect(() => {
        if (!persistQueryInUrl) {
            setLocalQuery('');
        }
    }, [persistQueryInUrl, speakerId]);

    useEffect(() => {
        axios
            .get(`/speaker/${encodeURIComponent(speakerId)}`)
            .then((res) => {
                setInfo(res.data || { speaker: null });
            })
            .catch((err) => {
                console.log(err);
                setInfo({ speaker: null });
            });

    }, [speakerId]);

    const [showAudioPlayerDrawer, setShowAudioPlayerDrawer] = useState(false);
    const trackInfoRef = useRef(null)

    const querySelected = (newQuery) => {
        const nextQuery = (newQuery || '').trim();
        searchContainerRef.current?.scrollTo(0, 0);

        if (persistQueryInUrl) {
            const nextSearchParams = new URLSearchParams(searchParams);
            if (nextQuery) {
                nextSearchParams.set('q', nextQuery);
            } else {
                nextSearchParams.delete('q');
            }
            setSearchParams(nextSearchParams);
        } else {
            setLocalQuery(nextQuery);
        }
    }

    const playTrack = useCallback((trackInfo, startSeconds) => () => {
        const trackId = trackInfo.id;
        trackInfoRef.current = trackInfo;
        console.log(trackInfo);
        loadTrack(trackInfo.mp3URL, startSeconds);
        setShowAudioPlayerDrawer(true);
        if (!!speakerId) {
            eventLog(2, {
                'speakerId': speakerId,
                'trackId': trackId,
            });
        }
    }, [speakerId]);

    const closeAudioPlayer = () => {
        setShowAudioPlayerDrawer(false);
        document.getElementById('audio').pause();
    };

    const noQueryView = (info) => {
        const hasHighlights = !!info?.previews?.length;
        const hasEpisodes = !!info?.tracks?.length;

        return (
            <div>
                <div style={{ paddingTop: '60px' }}>
                    <SpeakerHeader speaker={info.speaker} previews={info.previews} />
                </div>

                {hasHighlights && (
                    <div className='entityHighlightsActionGroup'>
                        <FullWidthButton
                            title='Highlights'
                            onClick={() => setShowHighlights(true)}
                            buttonClassName='entityHighlightsActionRow'
                        />
                    </div>
                )}

                <SearchSuggestions key={`topics:${speakerId}`} querySelected={querySelected} defaultSuggestions={defaultSuggestions} isLoading={suggestionsLoading} />

                <div className='entityInfoSectionTitle entityEpisodeSectionTitle'>Episodes</div>
                {hasEpisodes ? (
                    <div className='entityInfoContent entityEpisodeList trackInfoRowsVerticalList'>
                        {info.tracks.map((trackInfo, index) => (
                            <TrackInfoRow
                                key={trackInfo.id || index}
                                trackInfo={trackInfo}
                                preferredSpeakerId={info.speaker.id}
                                playTrack={playTrack}
                                includesVerticalCardPadding={false}
                                showsBottomDivider={index !== info.tracks.length - 1}
                            />
                        ))}
                    </div>
                ) : (
                    <div className='entityInfoEmptyState'>No episodes available.</div>
                )}
            </div>
        );
    };

    return (
        <div className='container appScreen' style={query ? { overflow: 'visible' } : undefined}>
            <AudioPlayerDrawer
                open={showAudioPlayerDrawer}
                onClose={closeAudioPlayer}
                source={trackInfoRef.current ? trackInfoRef.current.source : null}
                trackId={trackInfoRef.current ? trackInfoRef.current.id : null} />
            <HighlightsDrawer
                open={showHighlights}
                onClose={() => setShowHighlights(false)}
                onSearch={() => {
                    setShowHighlights(false);
                    setHighlightSearchEntry(entry => entry + 1);
                    requestAnimationFrame(() => searchContainerRef.current?.querySelector('.aa-Input')?.focus());
                }}
                previews={info?.previews || []}
                previewTitle={info?.speaker?.name || 'Highlights'}
                playbackOrigin={{ kind: 'speaker', id: info?.speaker?.id, name: info?.speaker?.name, imageURL: info?.speaker?.imageURL }}
            />

            {!info ?
                <div>
                    <Loading overrideStyle={{ padding: '50px', textAlign: 'center' }} />
                </div>
                :
                !info.speaker ?
                    !missingSpeaker ?
                        <div style={{ padding: '20px', fontSize: '24px', fontWeight: '600' }}>
                            Unknown speaker
                        </div> :
                        missingSpeaker
                    :
                    <SearchActivityProvider>
                        <div ref={searchContainerRef}>
                            <div>
                                <SearchBoxWithDefaultSuggestions
                                    key={`${speakerId}-${query || 'empty'}`}
                                    fixToTop={true}
                                    querySelected={querySelected}
                                    defaultSuggestions={defaultSuggestions}
                                    placeholder={`Search ${info.speaker.name}`}
                                    initialQuery={query}
                                />
                            </div>
                            <AlgoliaSpeakerSearch speaker={info.speaker} noQueryView={noQueryView(info)} initialQuery={query} initialResultsTab={highlightSearchEntry > 0 ? 'highlights' : undefined} searchIntentKey={highlightSearchEntry} />
                        </div>
                    </SearchActivityProvider>
            }
        </div >
    );
}

EntityInfo.propTypes = {

}

EntityInfo.defaultProps = {

}

const mapStateToProps = (state) => ({
})

const mapActionsToProps = {
}

export default connect(mapStateToProps, mapActionsToProps)(EntityInfo);
